import { Component, EventEmitter, Input, Output, AfterViewInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { environment } from '@env/environment';

declare const Stripe: any;

@Component({
  selector: 'app-payment-info-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCheckboxModule
  ],
  templateUrl: './payment-info-form.component.html',
  styleUrl: './payment-info-form.component.scss'
})
export class PaymentInfoFormComponent implements AfterViewInit, OnDestroy {
  @Input() clientSecret?: string;
  @Input() billingEmail?: string;
  @Output() paymentMethodCreated = new EventEmitter<string>();
  @Output() paymentSkipped = new EventEmitter<void>();

  billingForm: FormGroup;
  stripe: any;
  elements: any;
  cardElement: any;
  cardError: string | null = null;
  isProcessing = false;
  isCardComplete = false;
  private readonly isBrowser: boolean;

  constructor(
    private readonly fb: FormBuilder,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.billingForm = this.fb.group({
      nameOnCard: ['', Validators.required],
      billingAddress: this.fb.group({
        line1: [''],
        city: [''],
        state: [''],
        postal_code: [''],
        country: ['US']
      })
    });
  }

  get nameOnCardControl(): FormControl {
    return this.billingForm.get('nameOnCard') as FormControl;
  }

  get billingAddressGroup(): FormGroup {
    return this.billingForm.get('billingAddress') as FormGroup;
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.initializeStripe();
    }
  }

  ngOnDestroy(): void {
    if (this.cardElement) {
      this.cardElement.destroy();
    }
  }

  private async initializeStripe(): Promise<void> {
    try {
      // Load Stripe.js dynamically
      if (!Object.hasOwn(globalThis as object, 'Stripe')) {
        await this.loadStripeJs();
      }

      // Initialize Stripe with platform publishable key
      this.stripe = Stripe(environment.stripePlatformPublishableKey);

      // Create Elements instance
      this.elements = this.stripe.elements({
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#1a73e8',
            colorBackground: '#ffffff',
            colorText: '#30313d',
            colorDanger: '#df1b41',
            fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
            borderRadius: '8px',
            spacingUnit: '4px'
          },
          rules: {
            '.Input': {
              border: '1px solid #e0e0e0',
              boxShadow: 'none',
              padding: '12px'
            },
            '.Input:focus': {
              border: '2px solid #1a73e8',
              boxShadow: 'none'
            }
          }
        }
      });

      // Create and mount the card element
      this.cardElement = this.elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#30313d',
            '::placeholder': {
              color: '#aab7c4'
            }
          },
          invalid: {
            color: '#df1b41',
            iconColor: '#df1b41'
          }
        },
        hidePostalCode: true // We collect this separately
      });

      this.cardElement.mount('#card-element');

      // Listen for card changes
      this.cardElement.on('change', (event: any) => {
        this.cardError = event.error ? event.error.message : null;
        this.isCardComplete = event.complete;
      });

    } catch (error) {
      console.error('Error initializing Stripe:', error);
      this.cardError = 'Failed to load payment form. Please refresh the page.';
    }
  }

  private loadStripeJs(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Stripe.js'));
      document.head.appendChild(script);
    });
  }

  async confirmPaymentMethod(): Promise<void> {
    if (!this.stripe || !this.cardElement) {
      this.cardError = 'Payment system not initialized';
      return;
    }

    if (!this.isCardComplete) {
      this.cardError = 'Please complete your card details';
      return;
    }

    this.isProcessing = true;
    this.cardError = null;

    try {
      // Create payment method
      const { paymentMethod, error } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardElement,
        billing_details: {
          name: this.billingForm.get('nameOnCard')?.value,
          email: this.billingEmail,
          address: {
            line1: this.billingForm.get('billingAddress.line1')?.value,
            city: this.billingForm.get('billingAddress.city')?.value,
            state: this.billingForm.get('billingAddress.state')?.value,
            postal_code: this.billingForm.get('billingAddress.postal_code')?.value,
            country: this.billingForm.get('billingAddress.country')?.value
          }
        }
      });

      if (error) {
        this.cardError = error.message;
        this.isProcessing = false;
        return;
      }

      // Emit the payment method ID
      this.paymentMethodCreated.emit(paymentMethod.id);
      this.isProcessing = false;

    } catch (error: any) {
      console.error('Error creating payment method:', error);
      this.cardError = error.message || 'An unexpected error occurred';
      this.isProcessing = false;
    }
  }

  skipPayment(): void {
    this.paymentSkipped.emit();
  }

  canSubmit(): boolean {
    return this.isCardComplete && this.billingForm.get('nameOnCard')?.valid === true && !this.isProcessing;
  }
}

